import { axiosApiInstance } from 'Store';
import { CartDetailDto, CartProductDetailDto, InvoiceDataDto, OrderCreatedDto, OrderListDto, OrderListDtoOrderStatusEnum, ProductBaseInfoDto, ProductDetailDto, StatusDto } from 'utils/types/OrderModels';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

import { userApiRequests } from './user';

const addProductToCart = async (productId: number, quantity: number): Promise<CartProductDetailDto | null> => {
    return axiosApiInstance
        .put(`/order/cart/item/add?productId=${productId}&quantity=${quantity}`)
        .then((resp) => {
            activeNotification('Sukces!', 'Produkt został dodany do koszyka', 'success');

            return resp.data;
        })
        .catch(() => {
            activeNotification(
                'Problem z dodaniem produktu',
                'Nie udało się dodać produktu do koszyka. Spróbuj ponownie później.',
                'danger'
            );

            return null;
        });
};

const updateProductInCart = async (productId: number, quantity: number): Promise<CartProductDetailDto | null> => {
    return axiosApiInstance
        .put(`/order/cart/item/update?productId=${productId}&quantity=${quantity}`)
        .then((resp) => {
            activeNotification('Sukces!', 'Ilość produktu została zaktualizowana', 'success');

            return resp.data;
        })
        .catch(() => {
            activeNotification(
                'Problem z aktualizacją',
                'Nie udało się zaktualizować ilości produktu. Spróbuj ponownie później.',
                'danger'
            );

            return null;
        });
};

const getCartItems = async (): Promise<CartDetailDto | null> => {
    return axiosApiInstance
        .get('/order/cart/items')
        .then((resp) => resp.data)
        .catch((error) => {
            if (error.response?.status === 404) {
                return null;
            }
            const errorId = v4();
            userApiRequests.logException(error, errorId);

            return null;
        });
};

const deleteProductFromCart = async (productId: number): Promise<boolean | null> => {
    return axiosApiInstance
        .delete(`/order/cart/item/delete?productId=${productId}`)
        .then(() => {
            activeNotification('Sukces!', 'Produkt został usunięty z koszyka', 'success');

            return true;
        })
        .catch((error) => {
            if (error.response?.status === 404) {
                return null;
            }
            const errorId = v4();
            userApiRequests.logException(error, errorId);
            activeNotification(
                'Problem z usunięciem',
                'Nie udało się usunąć produktu z koszyka. Spróbuj ponownie później.',
                'danger'
            );

            return false;
        });
};

const clearCart = async (): Promise<boolean> => {
    return axiosApiInstance
        .delete('/order/cart/delete')
        .then(() => {
            activeNotification('Sukces!', 'Koszyk został wyczyszczony', 'success');

            return true;
        })
        .catch((error) => {
            // Jeśli 404 - koszyk już nie istnieje
            if (error.response?.status === 404) {
                return true; // jako success
            }
            const errorId = v4();
            userApiRequests.logException(error, errorId);
            activeNotification(
                'Problem z wyczyszczeniem koszyka',
                'Nie udało się wyczyścić koszyka. Spróbuj ponownie później.',
                'danger'
            );

            return false;
        });
};

const getAllProducts = async (): Promise<ProductBaseInfoDto[]> => {
    return axiosApiInstance
        .get('/order/product/all')
        .then((resp) => resp.data)
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Problem z pobraniem produktów',
                'Nie udało się pobrać listy produktów. Spróbuj ponownie później.',
                'warning'
            );

            return [];
        });
};

const getProductDetails = async (productId: number): Promise<ProductDetailDto | null> => {
    return axiosApiInstance
        .get(`/order/product/details?productId=${productId}`)
        .then((resp) => {
            return {
                ...resp.data,
                price: resp.data.price / 100
            };
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Problem z pobraniem szczegółów produktu',
                'Nie udało się pobrać szczegółów produktu. Spróbuj ponownie później.',
                'warning'
            );

            return null;
        });
};

const createOrder = async (invoiceData: InvoiceDataDto): Promise<OrderCreatedDto | null> => {
    return axiosApiInstance
        .post('/order/create', invoiceData)
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Problem ze złożeniem zamówienia',
                'Nie udało się złożyć zamówienia. Spróbuj ponownie później.',
                'danger'
            );

            return null;
        });
};

const getLastInvoiceData = async (): Promise<InvoiceDataDto | null> => {
    return axiosApiInstance
        .get('/order/invoice/last-data')
        .then((resp) => resp.data)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((err) => {
            return null;
        });
};

const payFreeOrder = async (orderNumber: string): Promise<boolean> => {
    return axiosApiInstance
        .put(`/order/transaction/pay/free?orderNumber=${orderNumber}`)
        .then(() => {
            activeNotification('Sukces!', 'Zamówienie zostało zrealizowane', 'success');

            return true;
        })
        .catch(() => {
            activeNotification(
                'Problem z realizacją zamówienia',
                'Nie udało się zrealizować zamówienia. Spróbuj ponownie później.',
                'danger'
            );

            return false;
        });
};

const getPayTransactionOrder = async (orderNumber: string): Promise<string | null> => {
    return axiosApiInstance
        .get(`/order/transaction/pay/przelewy24?orderNumber=${orderNumber}`)
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Problem z realizacją zamówienia',
                'Nie udało się zrealizować zamówienia. Spróbuj ponownie później.',
                'danger'
            );

            return null;
        });
};

const getOrderStatus = async (orderNumber: string): Promise<StatusDto | null> => {
    return axiosApiInstance
        .get(`/order/status?orderNumer=${orderNumber}`)
        .then((resp) => resp.data)
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);

            return null;
        });
};

const getOrderList = async (): Promise<OrderListDto[]> => {
    return axiosApiInstance
        .get('/order/list')
        .then((resp) => resp.data)
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Problem z pobraniem zamówień',
                'Nie udało się pobrać listy zamówień. Spróbuj ponownie później.',
                'warning'
            );

            return [];
        });
};

const getPendingSubscriptionOrder = async (): Promise<OrderListDto | null> => {
    return axiosApiInstance
        .get('/order/list')
        .then((resp) => {
            const orders: OrderListDto[] = resp.data;

            const pendingOrder = orders.find((order) => order.orderItems.some((item) => item.productName.toLowerCase().includes('subskrypcja') ||
                    item.productName.toLowerCase().includes('subscription')) &&
                (order.orderStatus === OrderListDtoOrderStatusEnum.REGISTERED ||
                 order.orderStatus === OrderListDtoOrderStatusEnum.PENDING));

            return pendingOrder || null;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);

            return null;
        });
};

export const purchaseApiRequests = {
    addProductToCart,
    updateProductInCart,
    getCartItems,
    deleteProductFromCart,
    clearCart,
    getAllProducts,
    getProductDetails,
    createOrder,
    getLastInvoiceData,
    payFreeOrder,
    getPayTransactionOrder,
    getOrderStatus,
    getOrderList,
    getPendingSubscriptionOrder
};
